import { useEffect, useState, useCallback } from 'react';
import {
  Plus, Trash, ArrowClockwise, CheckCircle, Warning, CaretDown,
  RocketLaunch, ClockCounterClockwise, Key, X, Eye, EyeSlash,
} from '@phosphor-icons/react';
import { api } from '../../lib/api-client';
import type {
  ReleaseChannel, ChannelAssetsResponse, ChannelAssetOnChannel,
  ChannelAssetAvailable, ReleaseHistoryEntry, ApiToken,
} from '@spine-workspace/shared';

// ── Helpers ───────────────────────────────────────────────────────────────────

function timeAgo(iso: string) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function TagBadge({ name }: { name: string }) {
  return (
    <span className="px-1.5 py-0.5 rounded text-[10px] font-sans bg-bg-input border border-border text-text-tertiary">
      {name}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    approved:  'bg-emerald-400/10 border-emerald-400/20 text-emerald-400',
    submitted: 'bg-blue-400/10 border-blue-400/20 text-blue-400',
    rejected:  'bg-red-400/10 border-red-400/20 text-red-400',
    draft:     'bg-bg-input border-border text-text-tertiary',
  };
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-sans border ${styles[status] ?? styles.draft}`}>
      {status}
    </span>
  );
}

function VersionBadge({ n, updated }: { n: number; updated?: boolean }) {
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-medium border ${
      updated
        ? 'bg-amber-400/10 border-amber-400/20 text-amber-400'
        : 'bg-bg-input border-border text-text-tertiary'
    }`}>
      v{n}
    </span>
  );
}

// ── Push Release Modal ────────────────────────────────────────────────────────

interface PushModalProps {
  channel: ReleaseChannel;
  selectedIds: Set<string>;
  onChannel: ChannelAssetOnChannel[];
  available: ChannelAssetAvailable[];
  onConfirm: (notes: string) => void;
  onClose: () => void;
  pushing: boolean;
}

function PushModal({ channel, selectedIds, onChannel, available, onConfirm, onClose, pushing }: PushModalProps) {
  const [notes, setNotes] = useState('');

  const onChannelMap = new Map(onChannel.map(a => [a.assetId, a]));

  const diffItems = [...selectedIds].map(id => {
    const existing = onChannelMap.get(id);
    const avail = available.find(a => a.assetId === id);
    const name = existing?.assetName ?? avail?.assetName ?? id;
    const latestV = existing?.latestApprovedVersionNumber ?? avail?.latestApprovedVersionNumber ?? 0;

    if (!existing) return { id, name, from: null, to: latestV, type: 'add' as const };
    if (!existing.isUpToDate) return { id, name, from: existing.currentVersionNumber, to: latestV, type: 'update' as const };
    return { id, name, from: existing.currentVersionNumber, to: latestV, type: 'same' as const };
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-bg-surface border border-border rounded-xl shadow-2xl w-full max-w-md mx-4 p-6 flex flex-col gap-5"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-base font-semibold text-text-primary">
            Push to <span className="text-accent">{channel.name}</span>
          </h2>
          <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
            <X size={16} />
          </button>
        </div>

        <div className="flex flex-col gap-1 max-h-64 overflow-y-auto">
          {diffItems.map(item => (
            <div key={item.id} className="flex items-center justify-between gap-3 py-1.5 px-2 rounded hover:bg-bg-hover">
              <span className="font-sans text-sm text-text-primary truncate">{item.name}</span>
              <div className="flex items-center gap-1.5 shrink-0">
                {item.type === 'add' && (
                  <span className="text-emerald-400 font-mono text-xs">+ v{item.to}</span>
                )}
                {item.type === 'update' && (
                  <span className="text-amber-400 font-mono text-xs">v{item.from} → v{item.to}</span>
                )}
                {item.type === 'same' && (
                  <span className="text-text-tertiary font-mono text-xs">= v{item.to}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="font-sans text-xs text-text-secondary">Release notes (optional)</label>
          <input
            type="text"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="e.g. Sprint 12 — updated hero animations"
            className="h-9 px-3 rounded-lg bg-bg-input border border-border text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent font-sans"
          />
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="h-9 px-4 rounded-lg text-sm font-sans text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-all"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(notes)}
            disabled={pushing}
            className="h-9 px-4 rounded-lg text-sm font-sans bg-accent text-white hover:bg-accent/90 disabled:opacity-50 flex items-center gap-2 transition-all"
          >
            {pushing ? <ArrowClockwise size={14} className="animate-spin" /> : <RocketLaunch size={14} />}
            Push {selectedIds.size} asset{selectedIds.size !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── API Tokens Panel ──────────────────────────────────────────────────────────

function ApiTokensPanel() {
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [newName, setNewName] = useState('');
  const [newToken, setNewToken] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get<{ tokens: ApiToken[] }>('/api/unity/tokens').then(r => setTokens(r.tokens)).catch(() => {});
  }, []);

  async function handleCreate() {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const r = await api.post<{ token: string; id: string; name: string }>('/api/unity/tokens', { name: newName.trim() });
      setNewToken(r.token);
      setShowToken(true);
      setNewName('');
      setTokens(prev => [{ id: r.id, name: r.name, scopes: 'read:assets', lastUsedAt: null, createdAt: new Date().toISOString() }, ...prev]);
    } catch {
      // ignore
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(id: string) {
    await api.delete(`/api/unity/tokens/${id}`).catch(() => {});
    setTokens(prev => prev.filter(t => t.id !== id));
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleCreate()}
          placeholder="Token name, e.g. Unity - Project Apex"
          className="flex-1 h-9 px-3 rounded-lg bg-bg-input border border-border text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent font-sans"
        />
        <button
          onClick={handleCreate}
          disabled={creating || !newName.trim()}
          className="h-9 px-3 rounded-lg bg-accent text-white text-sm font-sans hover:bg-accent/90 disabled:opacity-50 flex items-center gap-1.5"
        >
          <Plus size={14} />
          Generate
        </button>
      </div>

      {newToken && (
        <div className="rounded-lg border border-emerald-400/30 bg-emerald-400/5 p-3 flex flex-col gap-2">
          <p className="font-sans text-xs text-emerald-400">Token generated — copy it now, it won't be shown again.</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-xs text-text-primary bg-bg-input rounded px-2 py-1.5 overflow-hidden text-ellipsis whitespace-nowrap">
              {showToken ? newToken : '•'.repeat(48)}
            </code>
            <button onClick={() => setShowToken(v => !v)} className="text-text-tertiary hover:text-text-primary">
              {showToken ? <EyeSlash size={14} /> : <Eye size={14} />}
            </button>
            <button
              onClick={() => { navigator.clipboard.writeText(newToken); }}
              className="text-xs font-sans px-2 py-1 rounded bg-bg-input border border-border text-text-secondary hover:text-text-primary"
            >
              Copy
            </button>
            <button onClick={() => setNewToken(null)} className="text-text-tertiary hover:text-text-primary"><X size={14} /></button>
          </div>
        </div>
      )}

      <div className="flex flex-col divide-y divide-border">
        {tokens.length === 0 && (
          <p className="text-sm text-text-tertiary font-sans py-2">No tokens yet.</p>
        )}
        {tokens.map(t => (
          <div key={t.id} className="flex items-center justify-between py-2.5 gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Key size={13} className="text-text-tertiary shrink-0" />
              <span className="font-sans text-sm text-text-primary truncate">{t.name}</span>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="font-sans text-xs text-text-tertiary">
                {t.lastUsedAt ? `Last used ${timeAgo(t.lastUsedAt)}` : 'Never used'}
              </span>
              <button
                onClick={() => handleRevoke(t.id)}
                className="text-text-tertiary hover:text-red-400 transition-colors"
                title="Revoke token"
              >
                <Trash size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main ReleasesPage ─────────────────────────────────────────────────────────

export default function ReleasesPage() {
  const [channels, setChannels] = useState<ReleaseChannel[]>([]);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [channelData, setChannelData] = useState<ChannelAssetsResponse | null>(null);
  const [history, setHistory] = useState<ReleaseHistoryEntry[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showPushModal, setShowPushModal] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [tab, setTab] = useState<'assets' | 'history' | 'tokens'>('assets');
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelGated, setNewChannelGated] = useState(false);
  const [creatingChannel, setCreatingChannel] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'ok' | 'err' } | null>(null);

  // Load channels on mount
  useEffect(() => {
    api.get<{ channels: ReleaseChannel[] }>('/api/unity/channels')
      .then(r => {
        setChannels(r.channels);
        if (r.channels.length > 0 && !selectedChannelId) {
          setSelectedChannelId(r.channels[0].id);
        }
      })
      .catch(() => {});
  }, []);

  // Load assets + history when channel changes
  const loadChannel = useCallback(async (channelId: string) => {
    setLoadingAssets(true);
    setSelectedIds(new Set());
    try {
      const [assets, hist] = await Promise.all([
        api.get<ChannelAssetsResponse>(`/api/unity/channels/${channelId}/assets`),
        api.get<{ history: ReleaseHistoryEntry[] }>(`/api/unity/channels/${channelId}/history`),
      ]);
      setChannelData(assets);
      setHistory(hist.history);
    } catch {
      // ignore
    } finally {
      setLoadingAssets(false);
    }
  }, []);

  useEffect(() => {
    if (selectedChannelId) loadChannel(selectedChannelId);
  }, [selectedChannelId, loadChannel]);

  function showToast(msg: string, type: 'ok' | 'err') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  // Channel creation
  async function handleCreateChannel() {
    if (!newChannelName.trim()) return;
    setCreatingChannel(true);
    try {
      const r = await api.post<{ channel: ReleaseChannel }>('/api/unity/channels', { name: newChannelName.trim(), requiresApproval: newChannelGated });
      setChannels(prev => [...prev, r.channel]);
      setSelectedChannelId(r.channel.id);
      setNewChannelName('');
      setNewChannelGated(false);
      showToast(`Channel "${r.channel.name}" created`, 'ok');
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Failed to create channel', 'err');
    } finally {
      setCreatingChannel(false);
    }
  }

  async function handleDeleteChannel(id: string) {
    await api.delete(`/api/unity/channels/${id}`).catch(() => {});
    const remaining = channels.filter(c => c.id !== id);
    setChannels(remaining);
    if (selectedChannelId === id) {
      setSelectedChannelId(remaining[0]?.id ?? null);
      setChannelData(null);
    }
  }

  // Asset selection
  function toggleAll() {
    if (!channelData) return;
    const all = [
      ...channelData.onChannel.map(a => a.assetId),
      ...channelData.available.map(a => a.assetId),
    ];
    const allSelected = all.every(id => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(all));
  }

  function toggleAsset(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  // Push release
  async function handlePush(notes: string) {
    if (!selectedChannelId || selectedIds.size === 0) return;
    setPushing(true);
    try {
      const r = await api.post<{ released: number }>('/api/unity/releases', {
        channelId: selectedChannelId,
        assetIds: [...selectedIds],
        releaseNotes: notes || undefined,
      });
      showToast(`✓ ${r.released} asset${r.released !== 1 ? 's' : ''} pushed`, 'ok');
      setShowPushModal(false);
      setSelectedIds(new Set());
      await loadChannel(selectedChannelId);
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Push failed', 'err');
    } finally {
      setPushing(false);
    }
  }

  // Rollback
  async function handleRollback(entry: ReleaseHistoryEntry) {
    if (!selectedChannelId) return;
    try {
      await api.post('/api/unity/releases', {
        channelId: selectedChannelId,
        assetVersions: [{ assetId: entry.assetId, versionId: entry.versionId }],
        releaseNotes: `Rollback to v${entry.versionNumber}`,
      });
      showToast(`Rolled back ${entry.assetName} to v${entry.versionNumber}`, 'ok');
      await loadChannel(selectedChannelId);
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Rollback failed', 'err');
    }
  }

  const selectedChannel = channels.find(c => c.id === selectedChannelId);
  const allAssetCount = channelData
    ? channelData.onChannel.length + channelData.available.length
    : 0;
  const allSelected = allAssetCount > 0 && allAssetCount === selectedIds.size;

  return (
    <div className="flex flex-col gap-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-lg shadow-lg font-sans text-sm font-medium ${
          toast.type === 'ok' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {toast.msg}
        </div>
      )}

      {/* Push modal */}
      {showPushModal && selectedChannel && channelData && (
        <PushModal
          channel={selectedChannel}
          selectedIds={selectedIds}
          onChannel={channelData.onChannel}
          available={channelData.available}
          onConfirm={handlePush}
          onClose={() => setShowPushModal(false)}
          pushing={pushing}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-xl font-semibold text-text-primary">Unity Releases</h2>
          <p className="font-sans text-sm text-text-tertiary mt-0.5">Push approved assets to Unity projects via release channels</p>
        </div>
        {selectedIds.size > 0 && (
          <button
            onClick={() => setShowPushModal(true)}
            className="h-9 px-4 rounded-lg bg-accent text-white text-sm font-sans hover:bg-accent/90 flex items-center gap-2"
          >
            <RocketLaunch size={14} />
            Push {selectedIds.size} to {selectedChannel?.name ?? '…'}
          </button>
        )}
      </div>

      <div className="flex gap-6">
        {/* Left: channel list */}
        <div className="w-52 shrink-0 flex flex-col gap-2">
          <p className="font-sans text-xs font-medium text-text-tertiary uppercase tracking-wider px-1">Channels</p>
          <div className="flex flex-col gap-0.5">
            {channels.map(ch => (
              <div
                key={ch.id}
                className={`group flex items-center justify-between px-2.5 py-2 rounded-lg cursor-pointer transition-all ${
                  selectedChannelId === ch.id
                    ? 'bg-accent/15 text-accent'
                    : 'text-text-secondary hover:bg-bg-hover hover:text-text-primary'
                }`}
                onClick={() => setSelectedChannelId(ch.id)}
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="font-sans text-sm font-medium truncate">{ch.name}</span>
                  {ch.requiresApproval && (
                    <span className="shrink-0 px-1 py-0.5 rounded text-[9px] font-sans bg-amber-400/10 border border-amber-400/20 text-amber-400">prod</span>
                  )}
                </div>
                {ch.id !== 'profile_default' && (
                  <button
                    className="opacity-0 group-hover:opacity-100 text-text-tertiary hover:text-red-400 transition-all"
                    onClick={e => { e.stopPropagation(); handleDeleteChannel(ch.id); }}
                  >
                    <Trash size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
          {/* Create channel */}
          <div className="flex flex-col gap-1 mt-1">
            <div className="flex gap-1">
              <input
                type="text"
                value={newChannelName}
                onChange={e => setNewChannelName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreateChannel()}
                placeholder="New channel…"
                className="flex-1 h-8 px-2.5 rounded-lg bg-bg-input border border-border text-xs text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent font-sans min-w-0"
              />
              <button
                onClick={handleCreateChannel}
                disabled={creatingChannel || !newChannelName.trim()}
                className="w-8 h-8 rounded-lg bg-bg-input border border-border text-text-tertiary hover:text-text-primary hover:border-accent disabled:opacity-40 flex items-center justify-center"
              >
                <Plus size={12} />
              </button>
            </div>
            <label className="flex items-center gap-1.5 cursor-pointer px-0.5">
              <input
                type="checkbox"
                checked={newChannelGated}
                onChange={e => setNewChannelGated(e.target.checked)}
                className="w-3 h-3 rounded accent-accent"
              />
              <span className="font-sans text-[10px] text-text-tertiary">Approved only</span>
            </label>
          </div>
        </div>

        {/* Right: main content */}
        <div className="flex-1 min-w-0 flex flex-col gap-4">
          {!selectedChannelId && (
            <div className="text-center py-16 text-text-tertiary font-sans text-sm">
              Create a channel to get started
            </div>
          )}

          {selectedChannelId && (
            <>
              {/* Tabs */}
              <div className="flex gap-0 border-b border-border-subtle">
                {([
                  { key: 'assets',  label: 'Assets' },
                  { key: 'history', label: 'History' },
                  { key: 'tokens',  label: 'API Tokens' },
                ] as { key: typeof tab; label: string }[]).map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setTab(key)}
                    className={`px-4 py-2.5 font-sans text-sm font-medium border-b-2 -mb-px transition-all ${
                      tab === key
                        ? 'text-accent border-accent'
                        : 'text-text-secondary hover:text-text-primary border-transparent'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Assets tab */}
              {tab === 'assets' && (
                <div className="flex flex-col gap-3">
                  {loadingAssets && (
                    <div className="flex items-center gap-2 text-text-tertiary font-sans text-sm py-4">
                      <ArrowClockwise size={14} className="animate-spin" /> Loading…
                    </div>
                  )}

                  {!loadingAssets && channelData && (
                    <>
                      {/* Select all bar */}
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleAll}
                            className="w-4 h-4 rounded accent-accent"
                          />
                          <span className="font-sans text-sm text-text-secondary">
                            {allAssetCount} asset{allAssetCount !== 1 ? 's' : ''}
                          </span>
                        </label>
                        {selectedIds.size > 0 && (
                          <span className="font-sans text-xs text-text-tertiary">
                            {selectedIds.size} selected
                          </span>
                        )}
                      </div>

                      {/* On channel */}
                      {channelData.onChannel.length > 0 && (
                        <div className="flex flex-col gap-1">
                          <p className="font-sans text-xs font-medium text-text-tertiary uppercase tracking-wider">
                            On channel ({channelData.onChannel.length})
                          </p>
                          {channelData.onChannel.map(a => (
                            <AssetRow
                              key={a.assetId}
                              id={a.assetId}
                              name={a.assetName}
                              tags={a.tags.map(t => t.name)}
                              versionNumber={a.currentVersionNumber}
                              latestVersionNumber={a.latestApprovedVersionNumber}
                              submissionStatus={a.latestVersionStatus ?? a.submissionStatus}
                              isUpToDate={a.isUpToDate}
                              releasedAt={a.releasedAt}
                              selected={selectedIds.has(a.assetId)}
                              onToggle={() => toggleAsset(a.assetId)}
                            />
                          ))}
                        </div>
                      )}

                      {/* Available */}
                      {channelData.available.length > 0 && (
                        <div className="flex flex-col gap-1">
                          <p className="font-sans text-xs font-medium text-text-tertiary uppercase tracking-wider">
                            Available to add ({channelData.available.length})
                          </p>
                          {channelData.available.map(a => (
                            <AssetRow
                              key={a.assetId}
                              id={a.assetId}
                              name={a.assetName}
                              tags={a.tags.map(t => t.name)}
                              versionNumber={null}
                              latestVersionNumber={a.latestApprovedVersionNumber}
                              submissionStatus={a.submissionStatus}
                              isUpToDate={false}
                              releasedAt={null}
                              selected={selectedIds.has(a.assetId)}
                              onToggle={() => toggleAsset(a.assetId)}
                            />
                          ))}
                        </div>
                      )}

                      {allAssetCount === 0 && (
                        <div className="text-center py-12 text-text-tertiary font-sans text-sm">
                          No approved assets yet. Approve asset versions in the Assets tab first.
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* History tab */}
              {tab === 'history' && (
                <div className="flex flex-col divide-y divide-border-subtle">
                  {history.length === 0 && (
                    <p className="font-sans text-sm text-text-tertiary py-6 text-center">No releases yet.</p>
                  )}
                  {history.map(entry => (
                    <div key={entry.id} className="flex items-center justify-between gap-3 py-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <ClockCounterClockwise size={14} className="text-text-tertiary shrink-0" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-sans text-sm text-text-primary truncate">{entry.assetName}</span>
                            <VersionBadge n={entry.versionNumber} />
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="font-sans text-xs text-text-tertiary">{timeAgo(entry.releasedAt)}</span>
                            <span className="text-text-muted text-xs">·</span>
                            <span className="font-sans text-xs text-text-tertiary">{entry.releasedByName}</span>
                            {entry.releaseNotes && (
                              <>
                                <span className="text-text-muted text-xs">·</span>
                                <span className="font-sans text-xs text-text-tertiary italic truncate max-w-40">{entry.releaseNotes}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRollback(entry)}
                        title="Rollback to this version"
                        className="shrink-0 text-text-tertiary hover:text-accent transition-colors"
                      >
                        <ArrowClockwise size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* API Tokens tab */}
              {tab === 'tokens' && <ApiTokensPanel />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Asset Row ─────────────────────────────────────────────────────────────────

interface AssetRowProps {
  id: string;
  name: string;
  tags: string[];
  versionNumber: number | null;
  latestVersionNumber: number;
  submissionStatus: string;
  isUpToDate: boolean;
  releasedAt: string | null;
  selected: boolean;
  onToggle: () => void;
}

function AssetRow({
  name, tags, versionNumber, latestVersionNumber, submissionStatus, isUpToDate, releasedAt, selected, onToggle
}: AssetRowProps) {
  const notOnChannel = versionNumber === null;
  const hasUpdate = !notOnChannel && !isUpToDate;

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all border ${
        selected
          ? 'bg-accent/8 border-accent/30'
          : 'border-transparent hover:bg-bg-hover'
      }`}
      onClick={onToggle}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        onClick={e => e.stopPropagation()}
        className="w-4 h-4 rounded accent-accent shrink-0"
      />

      <div className="flex items-center gap-2 flex-1 min-w-0">
        {/* Status icon */}
        {notOnChannel ? (
          <CaretDown size={12} className="text-text-tertiary shrink-0" />
        ) : hasUpdate ? (
          <Warning size={12} className="text-amber-400 shrink-0" />
        ) : (
          <CheckCircle size={12} className="text-emerald-400 shrink-0" />
        )}

        <span className="font-sans text-sm text-text-primary truncate">{name}</span>

        <StatusBadge status={submissionStatus} />

        <div className="flex items-center gap-1 flex-wrap">
          {tags.slice(0, 2).map(t => <TagBadge key={t} name={t} />)}
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {notOnChannel ? (
          <VersionBadge n={latestVersionNumber} />
        ) : hasUpdate ? (
          <>
            <VersionBadge n={versionNumber!} />
            <span className="text-text-tertiary text-xs">→</span>
            <VersionBadge n={latestVersionNumber} updated />
          </>
        ) : (
          <VersionBadge n={versionNumber!} />
        )}

        {releasedAt && (
          <span className="font-sans text-xs text-text-tertiary w-16 text-right">{timeAgo(releasedAt)}</span>
        )}
      </div>
    </div>
  );
}
